
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { ResetPasswordLoading } from "@/components/auth/ResetPasswordLoading";
import { InvalidResetLink } from "@/components/auth/InvalidResetLink";
import { ResetPasswordForm } from "@/components/auth/ResetPasswordForm";
import { useToast } from "@/components/ui/use-toast";

export default function ResetPassword() {
  const [hasValidSession, setHasValidSession] = useState<boolean | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Check if this is a PASSWORD_RECOVERY event
  useEffect(() => {
    const handleAuthStateChange = supabase.auth.onAuthStateChange((event, session) => {
      console.log("Auth state event in ResetPassword:", event);
      
      if (event === "PASSWORD_RECOVERY") {
        console.log("Password recovery event detected in ResetPassword");
        setIsPasswordRecovery(true);
        
        if (session?.user?.email) {
          setUserEmail(session.user.email);
          setHasValidSession(true);
        }
      }
    });

    // Clean up the subscription
    return () => {
      handleAuthStateChange.data.subscription.unsubscribe();
    };
  }, []);

  // Check if there's a valid session
  useEffect(() => {
    if (isPasswordRecovery) {
      return; // Already handled by the auth state change
    }
    
    const checkSession = async () => {
      try {
        console.log("Checking session in ResetPassword");
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Error checking session:", error);
          setHasValidSession(false);
          return;
        }
        
        if (data.session) {
          console.log("Valid session found:", data.session.user?.email);
          setHasValidSession(true);
          setUserEmail(data.session.user?.email || null);
        } else {
          console.log("No valid session found");
          setHasValidSession(false);
        }
      } catch (err) {
        console.error("Unexpected error checking session:", err);
        setHasValidSession(false);
      }
    };

    checkSession();
  }, [isPasswordRecovery]);

  if (hasValidSession === null) {
    return <ResetPasswordLoading />;
  }

  if (hasValidSession === false) {
    return <InvalidResetLink />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Link to="/" className="inline-flex items-center">
            <span className="text-2xl font-bold text-purple-600">OG</span>
            <span className="text-2xl font-bold">MAI</span>
          </Link>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Reset your password</h2>
          <p className="mt-2 text-sm text-gray-600">Enter a new password for your account</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Create New Password</CardTitle>
            <CardDescription>Your password must be at least 8 characters long</CardDescription>
          </CardHeader>
          <CardContent>
            <ResetPasswordForm userEmail={userEmail} isPasswordRecovery={isPasswordRecovery} />
          </CardContent>
          <CardFooter className="flex justify-center">
            <p className="text-sm text-gray-600">
              Remember your password?{" "}
              <Link to="/login" className="font-medium text-purple-600 hover:text-purple-500">
                Sign in
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
